import React, { useEffect, useReducer } from 'react';
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Link, IconButton, Typography, TextField, InputAdornment, MenuItem, TablePagination, Select, SelectChangeEvent } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { DirectionsDriverDto, DirectionsFilter, ReferralStatus, ReferralStatusUtil } from '../../models/analytics/directions/directionsModels';
import { ROUTES } from '../../constants/routes';
import { analyticsService } from '../../services/analyticsService';
import { medicalCheckupDocumentsService } from '../../services/medicalCheckupDocumentsService';
import Spinner from '../common/Spinner';
import { DateUtils } from '../../helpers/dateUtils';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import { driversService } from '../../services/driversService';
import { DirectionsHistoryModal } from './DirectionsHistoryModal';
import { DriverMedicalCheckupsHistoryModal } from '../medical-checkups/details/DriverMedicalCheckupsHistoryModal';
import { DirectionsDocumentsModal } from './DirectionsDocumentsModal';
import { DatePickerFilter } from '../common/DatePickerFilter';
import ErrorAlert from '../common/ErrorAlert';
import { InlineDatePicker } from '../common/DatePickerLocalized';


export const DirectionsTable: React.FC = () => {
    type State = {
        rows: DirectionsDriverDto[];
        loading: boolean;
        error: string | null;
        page: number;
        rowsPerPage: number;
        totalCount: number;
        filter: {
            fromDate: string | null;
            toDate: string | null;
            fio: string;
            company: CompanyShortDto | null;
            status: 'all' | ReferralStatus;
        };
        // Status change history modal ("История изменений")
        historyModalOpen: boolean;
        historyDriverId: number | null;
        historyDriverFio: string;
        // Medical checkups history modal ("История осмотров")
        medicalCheckupsHistoryModalOpen: boolean;
        medicalCheckupsHistoryDriverId: number | null;
        medicalCheckupsHistoryDriverFio: string;
        // Documents modal ("История направлений")
        documentsModalOpen: boolean;
        documentsModalDriverFio: string;
        documentsModalDocuments: DirectionsDriverDto['documents'];
    };

    const initialState: State = {
        rows: [],
        loading: false,
        error: null,
        page: 0,
        rowsPerPage: 15,
        totalCount: 0,
        filter: {
            fromDate: null,
            toDate: null,
            fio: '',
            company: null,
            status: 'all',
        },
        historyModalOpen: false,
        historyDriverId: null,
        historyDriverFio: '',
        medicalCheckupsHistoryModalOpen: false,
        medicalCheckupsHistoryDriverId: null,
        medicalCheckupsHistoryDriverFio: '',
        documentsModalOpen: false,
        documentsModalDriverFio: '',
        documentsModalDocuments: [],
    };

    type Action =
        | { type: 'SET_ROWS'; payload: DirectionsDriverDto[] }
        | { type: 'SET_LOADING'; payload: boolean }
        | { type: 'SET_ERROR'; payload: string | null }
        | { type: 'SET_PAGE'; payload: number }
        | { type: 'SET_ROWS_PER_PAGE'; payload: number }
        | { type: 'SET_TOTAL_COUNT'; payload: number }
        | { type: 'SET_FILTER'; payload: Partial<State['filter']> }
        | { type: 'UPDATE_ROW'; payload: DirectionsDriverDto }
        | { type: 'OPEN_HISTORY'; payload: { id: number; fio: string } }
        | { type: 'CLOSE_HISTORY' }
        | { type: 'OPEN_MEDICAL_CHECKUPS_HISTORY'; payload: { id: number; fio: string } }
        | { type: 'CLOSE_MEDICAL_CHECKUPS_HISTORY' }
        | { type: 'OPEN_DOCUMENTS_MODAL'; payload: { fio: string; documents: DirectionsDriverDto['documents'] } }
        | { type: 'CLOSE_DOCUMENTS_MODAL' };

    const reducer = (state: State, action: Action): State => {
        switch (action.type) {
            case 'SET_ROWS':
                return { ...state, rows: action.payload, loading: false };
            case 'SET_LOADING':
                return { ...state, loading: action.payload };
            case 'SET_ERROR':
                return { ...state, error: action.payload };
            case 'SET_PAGE':
                return { ...state, page: action.payload };
            case 'SET_ROWS_PER_PAGE':
                return { ...state, rowsPerPage: action.payload, page: 0 };
            case 'SET_TOTAL_COUNT':
                return { ...state, totalCount: action.payload };
            case 'SET_FILTER':
                return { ...state, filter: { ...state.filter, ...action.payload }, page: 0 };
            case 'UPDATE_ROW':
                return { ...state, rows: state.rows.map(r => r.driverId === action.payload.driverId ? action.payload : r) };
            case 'OPEN_HISTORY':
                return { ...state, historyModalOpen: true, historyDriverId: action.payload.id, historyDriverFio: action.payload.fio };
            case 'CLOSE_HISTORY':
                return { ...state, historyModalOpen: false, historyDriverId: null, historyDriverFio: '' };
            case 'OPEN_MEDICAL_CHECKUPS_HISTORY':
                return { ...state, medicalCheckupsHistoryModalOpen: true, medicalCheckupsHistoryDriverId: action.payload.id, medicalCheckupsHistoryDriverFio: action.payload.fio };
            case 'CLOSE_MEDICAL_CHECKUPS_HISTORY':
                return { ...state, medicalCheckupsHistoryModalOpen: false, medicalCheckupsHistoryDriverId: null, medicalCheckupsHistoryDriverFio: '' };
            case 'OPEN_DOCUMENTS_MODAL':
                return { ...state, documentsModalOpen: true, documentsModalDriverFio: action.payload.fio, documentsModalDocuments: action.payload.documents };
            case 'CLOSE_DOCUMENTS_MODAL':
                return { ...state, documentsModalOpen: false, documentsModalDriverFio: '', documentsModalDocuments: [] };
            default:
                return state;
        }
    };

    const [state, dispatch] = useReducer(reducer, initialState);

    const load = async () => {
        dispatch({ type: 'SET_LOADING', payload: true });
        dispatch({ type: 'SET_ERROR', payload: null });
        try {
            // Transform local filter to API filter
            const apiFilter: DirectionsFilter = {
                fromDate: state.filter.fromDate ? DateUtils.dateToISO(state.filter.fromDate) : null,
                toDate: state.filter.toDate ? DateUtils.dateToISO(state.filter.toDate) : null,
                fio: state.filter.fio || undefined,
                companyId: state.filter.company?.id,
                statuses: state.filter.status === 'all' ? undefined : [state.filter.status as ReferralStatus],
                page: state.page,
                pageSize: state.rowsPerPage,
            };

            const resp = await analyticsService.getDirections(apiFilter);
            dispatch({ type: 'SET_ROWS', payload: resp.data.data.items });
            dispatch({ type: 'SET_TOTAL_COUNT', payload: resp.data.data.count });
        } catch (e: any) {
            dispatch({ type: 'SET_ERROR', payload: e.message ?? 'Ошибка загрузки' });
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    const updateDriver = async (driverId: number, patch: Partial<{ referralCallDate: string | null; referralStatus: ReferralStatus }>) => {
        await driversService.updateReferralStatus(driverId, patch);
    };

    const handleDateChange = async (row: DirectionsDriverDto, newDate: string | null) => {
        try {
            await updateDriver(row.driverId, { referralCallDate: newDate });
            dispatch({ type: 'UPDATE_ROW', payload: { ...row, referralCallDate: newDate } });
        } catch (err: any) {
            dispatch({ type: 'SET_ERROR', payload: err.message ?? 'Ошибка обновления' });
        }
    };

    const handleStatusChange = async (row: DirectionsDriverDto, newStatus: ReferralStatus) => {
        try {
            await updateDriver(row.driverId, { referralStatus: newStatus });
            dispatch({ type: 'UPDATE_ROW', payload: { ...row, referralStatus: newStatus } });
        } catch (err: any) {
            dispatch({ type: 'SET_ERROR', payload: err.message ?? 'Ошибка обновления' });
        }
    };

    const handleDocumentDownload = async (documentId: number) => {
        try {
            const response = await medicalCheckupDocumentsService.getDocumentDownloadLink(documentId);
            const url = response.data.data.link;
            window.open(url, '_blank');
        } catch (error) {
            console.error('Error downloading document:', error);
            dispatch({ type: 'SET_ERROR', payload: 'Ошибка загрузки документа' });
        }
    };

    function renderExternalLink(text: string, href: string): JSX.Element {
        return (
            <Link href={href} target="_blank" rel="noreferrer">
                {text}
                <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
            </Link>
        );
    }

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.filter]);

    return (
        <Box>
            {state.error && (
                <ErrorAlert
                    message={state.error}
                    onClose={() => dispatch({ type: 'SET_ERROR', payload: '' })}
                    autoCloseDuration={10000}
                />
            )}
            {state.loading && <Spinner />}

            {/* First row: Start Date, End Date, FIO */}
            <Box display="flex" alignItems="center" marginBottom={1} mt={2.5}>
                <DatePickerFilter
                    label="Начало периода"
                    name="fromDate"
                    value={state.filter.fromDate}
                    onChange={(value, name) => dispatch({ type: 'SET_FILTER', payload: { fromDate: typeof value === 'string' ? value : null } })}
                    style={{ width: 300, marginRight: 10, marginTop: 4 }}
                />

                <DatePickerFilter
                    label="Конец периода"
                    name="toDate"
                    value={state.filter.toDate}
                    onChange={(value, name) => dispatch({ type: 'SET_FILTER', payload: { toDate: typeof value === 'string' ? value : null } })}
                    style={{ width: 300, marginRight: 10, marginTop: 4 }}
                />

                <TextField
                    label="ФИО сотрудника"
                    name="fio"
                    value={state.filter.fio}
                    onChange={(e) => dispatch({ type: 'SET_FILTER', payload: { fio: e.target.value } })}
                    variant="outlined"
                    margin="dense"
                    size="small"
                    style={{ width: 300, marginRight: 10 }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={() => { dispatch({ type: 'SET_PAGE', payload: 0 }); load(); }}>
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {/* Second row: Company, Status */}
            <Box display="flex" alignItems="center" marginBottom={1}>
                <CompanyAutocomplete
                    company={state.filter.company}
                    onCompanyChange={(c) => dispatch({ type: 'SET_FILTER', payload: { company: c } })}
                    style={{ width: 300, marginRight: 10, marginTop: 4 }}
                />

                <TextField
                    label="Статус"
                    select
                    variant="outlined"
                    margin="dense"
                    size="small"
                    value={state.filter.status}
                    onChange={(e) => {
                        const val = e.target.value as 'all' | ReferralStatus;
                        dispatch({ type: 'SET_FILTER', payload: { status: val } });
                    }}
                    style={{ width: 300, marginRight: 10, marginTop: 4 }}
                >
                    <MenuItem value="all">Все</MenuItem>
                    <MenuItem value={ReferralStatus.Undefined}>{ReferralStatusUtil.getStringValue(ReferralStatus.Undefined)}</MenuItem>
                    <MenuItem value={ReferralStatus.NoAnswer}>{ReferralStatusUtil.getStringValue(ReferralStatus.NoAnswer)}</MenuItem>
                    <MenuItem value={ReferralStatus.ConversationHeld}>{ReferralStatusUtil.getStringValue(ReferralStatus.ConversationHeld)}</MenuItem>
                    <MenuItem value={ReferralStatus.CertificateReceived}>{ReferralStatusUtil.getStringValue(ReferralStatus.CertificateReceived)}</MenuItem>
                    <MenuItem value={ReferralStatus.NormalizedWithoutCertificate}>{ReferralStatusUtil.getStringValue(ReferralStatus.NormalizedWithoutCertificate)}</MenuItem>
                </TextField>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small" sx={{ '& td': { py: 0.5, px: 1 }, '& th': { py: 0.75, px: 1 } }}>
                    <TableHead>
                        <TableRow>
                            <TableCell>История направлений</TableCell>
                            <TableCell>ФИО сотрудника</TableCell>
                            <TableCell>Клиент</TableCell>
                            <TableCell>Диспетчер</TableCell>
                            <TableCell>История осмотров</TableCell>
                            <TableCell>Дата звонка</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>История изменений</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {state.rows.map((row) => (
                            <TableRow key={row.driverId}>
                                <TableCell>
                                    <Link
                                        component="button"
                                        underline="hover"
                                        onClick={() => dispatch({
                                            type: 'OPEN_DOCUMENTS_MODAL',
                                            payload: {
                                                fio: `${row.lastName} ${row.firstName}${row.middleName ? ' ' + row.middleName : ''}`,
                                                documents: row.documents
                                            }
                                        })}
                                    >
                                        Направления
                                    </Link>
                                </TableCell>
                                <TableCell>
                                    {renderExternalLink(
                                        `${row.lastName} ${row.firstName}${row.middleName ? ' ' + row.middleName : ''}`,
                                        ROUTES.drivers.idPath.replace(':id', String(row.driverId))
                                    )}
                                </TableCell>
                                <TableCell>
                                    {renderExternalLink(row.companyName, ROUTES.clients.idPath.replace(':id', String(row.companyId)))}
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'normal' }}>
                                    {row.dispatchers.map((d, i) => (
                                        <React.Fragment key={`${row.driverId}-${d.id}-${i}`}>
                                            <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
                                                {renderExternalLink(
                                                    `${d.lastName} ${d.firstName}${d.middleName ? ' ' + d.middleName : ''}`,
                                                    ROUTES.dispatchers.idPath.replace(':id', String(d.id))
                                                )}
                                            </Box>
                                            {i < row.dispatchers.length - 1 ? ', ' : ''}
                                        </React.Fragment>
                                    ))}
                                </TableCell>
                                <TableCell>
                                    <Link component="button" underline="hover" onClick={() => dispatch({ type: 'OPEN_MEDICAL_CHECKUPS_HISTORY', payload: { id: row.driverId, fio: `${row.lastName} ${row.firstName}${row.middleName ? ' ' + row.middleName : ''}` } })}>История</Link>
                                </TableCell>
                                <TableCell sx={{ minWidth: 90, whiteSpace: 'nowrap' }}>
                                    <Box sx={{
                                        width: '100%',
                                        minWidth: '90px',
                                        '& .MuiInputBase-root': {
                                            fontSize: '0.75rem',
                                            height: '28px'
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '4px 8px'
                                        }
                                    }}>
                                        <InlineDatePicker
                                            value={row.referralCallDate}
                                            onChange={(newValue) => handleDateChange(row, newValue)}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Select
                                        size="small"
                                        value={row.referralStatus}
                                        onChange={(e: SelectChangeEvent) => handleStatusChange(row, e.target.value as ReferralStatus)}
                                        sx={{
                                            width: '180px',
                                            fontSize: '0.75rem',
                                            height: '28px',
                                            '& .MuiSelect-select': {
                                                padding: '4px 8px',
                                                fontSize: '0.75rem'
                                            }
                                        }}
                                    >
                                        <MenuItem value={ReferralStatus.Undefined}>{ReferralStatusUtil.getStringValue(ReferralStatus.Undefined)}</MenuItem>
                                        <MenuItem value={ReferralStatus.NoAnswer}>{ReferralStatusUtil.getStringValue(ReferralStatus.NoAnswer)}</MenuItem>
                                        <MenuItem value={ReferralStatus.ConversationHeld}>{ReferralStatusUtil.getStringValue(ReferralStatus.ConversationHeld)}</MenuItem>
                                        <MenuItem value={ReferralStatus.CertificateReceived}>{ReferralStatusUtil.getStringValue(ReferralStatus.CertificateReceived)}</MenuItem>
                                        <MenuItem value={ReferralStatus.NormalizedWithoutCertificate}>{ReferralStatusUtil.getStringValue(ReferralStatus.NormalizedWithoutCertificate)}</MenuItem>
                                    </Select>
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" onClick={() => dispatch({ type: 'OPEN_HISTORY', payload: { id: row.driverId, fio: `${row.lastName} ${row.firstName}${row.middleName ? ' ' + row.middleName : ''}` } })}>
                                        <AccessTimeIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                <Typography>Всего: {state.totalCount}</Typography>
                <TablePagination
                    rowsPerPageOptions={[15, 30, 50]}
                    component="div"
                    count={state.totalCount}
                    rowsPerPage={state.rowsPerPage}
                    page={state.page}
                    onPageChange={(_, newPage) => dispatch({ type: 'SET_PAGE', payload: newPage })}
                    onRowsPerPageChange={(e) => { dispatch({ type: 'SET_ROWS_PER_PAGE', payload: parseInt(e.target.value, 10) }); }}
                    labelRowsPerPage={'Строк на странице:'}
                    labelDisplayedRows={({ from, to, count }: any) => `Показано ${from}-${to} из ${count}`}
                />
            </Box>

            <DirectionsHistoryModal
                open={state.historyModalOpen}
                onClose={() => dispatch({ type: 'CLOSE_HISTORY' })}
                driverId={state.historyDriverId}
                driverFio={state.historyDriverFio}
            />
            <DriverMedicalCheckupsHistoryModal
                open={state.medicalCheckupsHistoryModalOpen}
                onClose={() => dispatch({ type: 'CLOSE_MEDICAL_CHECKUPS_HISTORY' })}
                driverId={state.medicalCheckupsHistoryDriverId}
                driverFio={state.medicalCheckupsHistoryDriverFio}
            />
            <DirectionsDocumentsModal
                open={state.documentsModalOpen}
                onClose={() => dispatch({ type: 'CLOSE_DOCUMENTS_MODAL' })}
                driverFio={state.documentsModalDriverFio}
                documents={state.documentsModalDocuments}
                onDocumentDownload={handleDocumentDownload}
            />
        </Box>
    );
};
